import React from 'react';
import { Navigate, useLocation, type Location } from 'react-router-dom';
import { Permission } from './types';
import { usePermissions } from './usePermissions';
import { AuthContext } from '../contexts/AuthContext';
import { Loader } from '../components/ui/Loader';
import { usePermissionReadiness } from './usePermissionReadiness';
import { captureError } from '../services/MonitoringService';
import type { Capability as AccountCapability } from '../lib/auth/capabilities';
import type { Capability as ScopedCapability, ScopedContext } from '../lib/auth/scopedCapabilityResolver';
import {
  findCanonicalRouteRule,
  type RouteAuthorityMode,
} from '../lib/auth/routeRegistry';
import { resolveRouteAuthority } from '../lib/auth/routeAuthority';

interface ProtectedRouteProps {
  /**
   * Permission required to access the route (legacy string-based)
   * @deprecated Use `capability` + `contextResolver` instead
   */
  permission?: Permission | Permission[];
  
  /**
   * Typed capability identifier (CANONICAL - preferred)
   * e.g., 'project.manage', 'workspace.view'
   */
  capability?: AccountCapability | ScopedCapability;

  /**
   * Function to resolve scoped context from route params/state
   * Required when using typed capabilities
   * Can be synchronous or asynchronous
   * Should return null/undefined if context cannot be resolved
   */
  contextResolver?: (location: Location, pathname: string) => ScopedContext | undefined | Promise<ScopedContext | undefined>;
  
  /**
   * Component to render if user has permission
   */
  children: React.ReactNode;
  
  /**
   * Where to redirect if permission is denied
   */
  redirectTo?: string;
}

/**
 * Component for protecting routes with capability checks
 * 
 * CANONICAL: Use with `capability` + `contextResolver` for scoped authorization
 * LEGACY: Use with `permission` for backward compatibility (will be removed Phase 4)
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  permission,
  capability,
  contextResolver,
  children,
  redirectTo = '/unauthorized'
}) => {
  const { can, hasPermission: allowsPermission, hasAllPermissions } = usePermissions();
  const auth = React.useContext(AuthContext);
  const { isReady } = usePermissionReadiness();
  const location = useLocation();
  const authority = React.useMemo(() => resolveRouteAuthority(auth?.user ?? null), [auth?.user]);
  const routeRule = React.useMemo(() => findCanonicalRouteRule(location.pathname), [location.pathname]);
  const routeAuthorityMode: RouteAuthorityMode = routeRule?.authorityMode ?? 'account_capability';
  
  // State for async context resolution
  const [resolvedContext, setResolvedContext] = React.useState<ScopedContext | undefined | null>(undefined);
  const [contextLoading, setContextLoading] = React.useState(false);

  // Handle async context resolution
  React.useEffect(() => {
    if (!capability || !contextResolver) {
      setResolvedContext(null); // No context needed
      return;
    }

    setContextLoading(true);
    setResolvedContext(undefined); // Loading state

    const resolveContext = async () => {
      try {
        const result = contextResolver(location, location.pathname);
        
        // Handle both sync and async resolvers
        if (result instanceof Promise) {
          const context = await result;
          setResolvedContext(context || null);
        } else {
          setResolvedContext(result || null);
        }
      } catch (error) {
        console.warn('[ProtectedRoute] Context resolution error:', error);
        setResolvedContext(null); // Fail-closed on error
      } finally {
        setContextLoading(false);
      }
    };

    resolveContext();
  }, [capability, contextResolver, location]);

  // Check if user has required permission(s)
  const decision = React.useMemo(() => {
    if (!isReady) {
      return { allowed: false, reason: 'authority_loading' } as const;
    }
    if (contextLoading) {
      return { allowed: false, reason: 'context_loading' } as const;
    }

    // CANONICAL: Scoped capability check (NEW)
    if (capability) {
      if (routeAuthorityMode === 'resource_scoped') {
        if (!contextResolver) {
          return {
            allowed: false,
            reason: 'route_configuration_error: resource_scoped route missing context resolver',
          } as const;
        }
        if (resolvedContext === undefined) {
          return { allowed: false, reason: 'context_loading' } as const;
        }
        if (!resolvedContext) {
          return { allowed: false, reason: 'resource_context_denied' } as const;
        }
        const allowed = can(capability as ScopedCapability, resolvedContext);
        return {
          allowed,
          reason: allowed ? undefined : 'resource_scope_denied',
        } as const;
      }

      if (routeAuthorityMode === 'workspace_surface' && routeRule?.surface) {
        const allowed = authority.surfaceEntries[routeRule.surface] ?? false;
        return { allowed, reason: allowed ? undefined : `workspace_surface_denied:${routeRule.surface}` } as const;
      }

      const hasCapability = authority.capabilities.includes(capability as AccountCapability);
      if (routeAuthorityMode === 'platform_capability') {
        return {
          allowed: Boolean(authority.platformRole && hasCapability),
          reason: authority.platformRole && hasCapability ? undefined : 'missing_platform_capability',
        } as const;
      }

      return { allowed: hasCapability, reason: hasCapability ? undefined : 'missing_account_capability' } as const;
    }

    // LEGACY: Old permission string system (deprecated)
    if (permission) {
      if (Array.isArray(permission)) {
        return { allowed: hasAllPermissions(permission), reason: hasAllPermissions(permission) ? undefined : 'missing_legacy_permission' } as const;
      }
      return { allowed: allowsPermission(permission), reason: allowsPermission(permission) ? undefined : 'missing_legacy_permission' } as const;
    }

    // No authorization specified - deny
    return { allowed: false, reason: 'missing_authorization_contract' } as const;
  }, [
    isReady,
    contextLoading,
    capability,
    permission,
    contextResolver,
    resolvedContext,
    can,
    allowsPermission,
    hasAllPermissions,
    routeAuthorityMode,
    routeRule?.surface,
    authority.capabilities,
    authority.platformRole,
    authority.surfaceEntries,
  ]);

  // If auth or context is initializing, render a neutral loading state to avoid flicker
  if (decision.reason === 'authority_loading' || decision.reason === 'context_loading') {
    return (
      <div aria-busy="true" className="min-h-[120px] flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  
  // Redirect if no access
  if (!decision.allowed) {
    // Lightweight telemetry for unauthorized attempts (non-sensitive)
    try {
      const perms = capability || (Array.isArray(permission) ? permission.join(',') : permission);
      if (decision.reason?.startsWith('route_configuration_error')) {
        console.error(`[ProtectedRoute] ${decision.reason}`);
      }
      captureError('Unauthorized route access', {
        level: 'warning',
        tags: { route: location.pathname },
        extra: { requiredPermission: perms, reason: decision.reason }
      });
    } catch {
      // telemetry best-effort only
    }
    return <Navigate to={redirectTo} state={{ from: location, reason: decision.reason }} replace />;
  }
  
  // Render children if access granted
  return <>{children}</>;
};

// HOC variant removed to avoid react-refresh limitations; prefer using <ProtectedRoute> directly
